if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.torenaCompact(productElement);
      }

      /**
       * 压成参考图那个样子(May 2026-08-26)。
       *
       * 只做**减法和加一条链接**,不重排属性 —— 属性顺序(MODEL → COLOR → SIZE)
       * 走 CSS 的 order,因为每次换变体 Dawn 会整个替换 variant-selects,
       * 在这里用 JS 排,下一次点击就被冲掉了。
       */
      torenaCompact(productElement) {
        // 详情页专属样式不能套进弹窗 —— 两栏布局和大图比例会把版式撑坏
        productElement.classList.remove('torena-pdp');
        this.modalContent?.classList.add('torena-qa');
        this.classList.add('torena-qa-modal');
        // 弹窗外壳也要带上标记 —— 宽度和圆角是加在 __content 上的
        this.querySelector('.quick-add-modal__content')?.classList.add('torena-qa');

        /**
         * 主图 + 最多 4 张缩略图（May 2026-08-27:「下面分成两列出现其他SKU的商品」）。
         * 第一张当主图占满两列，其余在 CSS 里排成两列。
         * ⚠️ 上限 5 张：再多左列就比右边那一串属性还高，弹窗又要滚了。
         * 之前是「只留第一张」，所以下面没有任何缩略图可排。
         */
        productElement.querySelectorAll('.product__media-item').forEach((el, i) => i > 4 && el.remove());

        /**
         * ⚠️⚠️ **先把 Dawn 的商品页布局类摘掉,再谈版式。**
         * 它那些两栏规则藏在 `@media (min-width:750px)` 里、还带 `.product--large`
         * 这种前缀,权重和顺序都压过我的覆盖 —— 一条条去盖盖不完
         * (2026-08-26 内容被挤成一条 175px 的窄柱就是这么来的)。
         * 让它们**匹配不上**，两列宽度完全由 torena-quick-add.css 说了算。
         * 只摘布局类，功能类(product-form__input / quantity 那些)一个不动。
         */
        const LAYOUT_CLASSES = [
          'grid', 'grid--1-col', 'grid--2-col-tablet', 'grid__item',
          'product--large', 'product--medium', 'product--small',
          'product--columns', 'product--stacked', 'product--thumbnail',
          'product--thumbnail_slider', 'product--no-media',
          'product__column-sticky', 'product__info-wrapper--extra-padding',
        ];
        productElement
          .querySelectorAll('.product, .product__info-wrapper, .product__media-wrapper, .product__info-container')
          .forEach((el) => {
            LAYOUT_CLASSES.forEach((c) => el.classList.remove(c));
            [...el.classList].forEach((c) => {
              if (c.startsWith('product--mobile-') || c.startsWith('grid--')) el.classList.remove(c);
            });
          });

        /**
         * 「View product details」——参考图里价格下面那条下划线小链接。
         *
         * ⚠️ 2026-08-27 改回来了:之前把「图+标题+价格」搬进一个 head 块里,
         * 是为了做「图只压标题那一行、下面通栏」。May 后来要的是
         * **左边一列图、右边一列全部信息**(她的原话「图片在左，所有基本信息在右」),
         * 那正好就是 Dawn 原本的两栏结构 —— 不用动 DOM,CSS 给两列宽度就行。
         * 少一次 DOM 手术,换变体时也少一处可能对不上的地方。
         */
        const info = productElement.querySelector('.product__info-container');
        const priceEl = productElement.querySelector('[id^="price-"]');

        /**
         * MODEL 超过 10 个（5 行）就收起来，给一个小三角展开（May 2026-08-27）。
         *
         * ⚠️ 复选框和标签**放在 variant-selects 外面** —— 每次换变体 Dawn 会把
         * 整个 variant-selects 换成服务端新渲染的一份，放里面会连同展开状态一起被冲掉。
         * 放外面就只注入这一次,之后纯 CSS 控制,不用重新绑任何事件。
         */
        const vs = productElement.querySelector('variant-selects');

        /**
         * 款式名被省略号截断时的全名提示(title)**不在这里做** ——
         * 已经挪进 `snippets/product-variant-options.liquid`,详情页和弹窗共用那个 snippet,
         * 改一处两边都有。这里再补一遍等于两个地方维护同一件事。
         */
        if (vs && !productElement.querySelector('.torena-qa__more')) {
          const box = document.createElement('input');
          box.type = 'checkbox';
          box.className = 'torena-qa__more';
          box.id = `torena-qa-more-${productElement.dataset.section || 'x'}`;
          box.hidden = true;
          const lab = document.createElement('label');
          lab.className = 'torena-qa__more-label';
          lab.setAttribute('for', box.id);
          lab.textContent = 'Show all styles';
          vs.parentNode.insertBefore(box, vs);
          vs.parentNode.insertBefore(lab, vs.nextSibling);
          // 只有真的超过 10 个才露出这个三角
          const n = vs.querySelectorAll("[data-option-name='MODEL' i] label").length;
          lab.hidden = n <= 10;
        }
        const url = productElement.dataset.url || this.openedBy?.getAttribute('data-product-url');
        if (info && url && !productElement.querySelector('.torena-qa__details-link')) {
          const a = document.createElement('a');
          a.className = 'torena-qa__details-link';
          a.href = url;
          a.textContent = 'View product details';
          if (priceEl) priceEl.insertAdjacentElement('afterend', a);
          else info.appendChild(a);
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
